/** @jsx jsx */
import { Table } from "@cd2/components/build/es6"
import { H4 } from "@cd2/text/build/es6"
import { css, jsx } from "@emotion/core"
import * as dateFns from "date-fns"
import React from "react"
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from "recharts"
import { colors, themeColors } from "../../Components/EOSThemeProvider"
import { FloatingBox } from "../../Components/Misc/FloatingBox"
import { formatPrice } from "../../utils/utils"
import { collapseTagDataMapForStats, DashboardData, TagFilter } from "../../../shared/dashboardData"

import { ThemeProvider } from "@cd2/theme"

export function CustomerMonthlySpendGraph({
  data,
  currency,
  filterTag,
}: {
  data: Pick<DashboardData, "last12MonthsByTags" | "tags">
  currency: "" | "£" | "€" | "$"
  filterTag: TagFilter | null
}) {
  const spendingData = data.last12MonthsByTags.map(el => collapseTagDataMapForStats(el, filterTag))
  const graphData = [...spendingData].sort((a, b) => (a.monthRaw > b.monthRaw ? 1 : -1))
  return (
    <FloatingBox header={<H4>Customer Spending</H4>}>
      <div
        css={css`
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        `}
      >
        <div
          css={css`
            width: 100%;
            max-width: 100%;
            position: relative;
            overflow: hidden;
            border-radius: 3px;
            flex: 4 0 350px;
          `}
        >
          <ResponsiveContainer width={"100%"} minHeight={"400px"}>
            <ComposedChart data={graphData}>
              <CartesianGrid stroke="#e5e5e5" />
              <XAxis
                dataKey="monthRaw"
                tickFormatter={value => {
                  if (typeof value === "number" && value > 0) {
                    return dateFns.format(new Date(value), "MMM yy")
                  }
                  return value
                }}
              />
              <Bar
                yAxisId="count"
                dataKey="orderCount"
                name="Order Count"
                fill={colors.orders}
                barSize={8}
                opacity={0.5}
              />
              <Bar
                yAxisId="count"
                dataKey="quoteCount"
                name="Quote Count"
                fill={colors.quotes}
                barSize={8}
                opacity={0.5}
              />
              <Line
                name="Revenue"
                yAxisId="spend"
                dataKey="revenue"
                stroke={colors.invoices}
                strokeWidth={2}
                type="monotone"
              />
              <Line
                name="Gross Margin"
                yAxisId="spend"
                dataKey="grossMargin"
                stroke={colors.clients}
                strokeWidth={2}
                type="monotone"
              />
              <Tooltip
                labelFormatter={label => {
                  if (typeof label !== "number") {
                    return label
                  }
                  const date = new Date(label)
                  return <div>{dateFns.format(date, "MMM yyyy")}</div>
                }}
                formatter={(value: number, name: string) => {
                  const priceNames = ["Revenue", "Gross Margin"]
                  if (priceNames.includes(name)) {
                    return formatPrice(value, { currency, isPennies: true })
                  }
                  return value
                }}
                active={true}
                position={{ x: 100, y: 140 }}
              />
              <Legend />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {spendingData.sort((a, b) => (a.monthRaw > b.monthRaw ? -1 : 1)).length > 0 && (
          <CompactTable>
            <Table data={spendingData}>
              <Table.Column heading="Month">
                {row => (row.total ? `Total` : dateFns.format(new Date(row.monthRaw), "MMM yy"))}
              </Table.Column>
              <Table.Column heading="Revenue">
                {row => formatPrice(row.revenue, { currency, isPennies: true })}
              </Table.Column>
              <Table.Column heading="Gross Margin">
                {row => formatPrice(row.grossMargin, { currency, isPennies: true })}
              </Table.Column>
              <Table.Column heading="Rebate">
                {row => formatPrice(row.rebate, { currency, isPennies: true })}
              </Table.Column>
              <Table.Column heading="Margin %">
                {row =>
                  row.revenue ? ((row.grossMargin / row.revenue) * 100).toFixed(2) + "%" : "0%"
                }
              </Table.Column>
            </Table>
          </CompactTable>
        )}
      </div>
    </FloatingBox>
  )
}

export function CompactTable({ children }: { children: React.ReactNode }) {
  return (
    <div
      css={css`
        flex: 1 0 250px;
        background: #f5f5f5;
        border-radius: 12px;
        border: 1px solid #ddd;
        overflow: hidden;
      `}
    >
      <ThemeProvider.Extend
        theme={{
          table: {
            cells: {
              fontSize: 12,
            },
            cssHeadingRow: css`
              th {
                white-space: nowrap;
                border-bottom: 1px solid #ddd;
                height: 36px;
                padding: 2px 10px;
              }
            `,
            cssRow: css`
              border-bottom: 1px solid #ddd;
              background: ${themeColors.lightestGrey};
              color: #333;

              &:first-of-type {
                border-radius: 6px 6px 0 0;
              }

              td {
                height: 36px;
                padding: 2px 10px;
              }
            `,
          },
        }}
      >
        {children}
      </ThemeProvider.Extend>
    </div>
  )
}
